import { parseNumericValue } from "./numberFormatter";

export interface EthMarketAssetRoi {
  percentage?: number | string | null;
  percent?: number | string | null;
}

export interface EthMarketAssetInfo {
  asset?: string | null;
  cmc?: {
    description?: string | null;
  } | null;
}

export interface EthMarketAsset {
  price?: number | string | null;
  priceInZar?: number | string | null;
  market_cap?: number | string | null;
  volume_24h?: number | string | null;
  circulating_supply?: number | string | null;
  market_cap_rank?: number | null;
  max_supply?: number | string | null;
  total_supply?: number | string | null;
  ath?: number | string | null;
  ath_percentage?: number | string | null;
  percent_change_30d?: number | string | null;
  roi?: EthMarketAssetRoi | null;
  info?: EthMarketAssetInfo | null;
}

export interface YieldMarketData {
  price: number | string | null;
  priceInZar: number | string | null;
  marketCap: number | string | null;
  volume24h: number | string | null;
  circulatingSupply: number | string | null;
  rank: number | null;
  maxSupply: number | string | null;
  totalSupply: number | string | null;
  allTimeHigh: number | string | null;
  athPercentage: number | string | null;
  roiPercentage: number | string | null;
  percentChange30d: number | string | null;
  description: string | null;
}

export type MarketAssetsMap = Record<string, EthMarketAsset | null | undefined>;

export interface YieldBalanceAsset {
  asset?: string | null;
  availableAmount?: number | string | { $numberDecimal?: string } | null;
  valueInZar?: number | string | { $numberDecimal?: string } | null;
  valueInLocalCurrency?: number | string | { $numberDecimal?: string } | null;
}

export interface YieldTransactionRef {
  incomingAsset?: string | null;
  outgoingAsset?: string | null;
  incomingAmount?: number | string | { $numberDecimal?: string } | null;
  outgoingAmount?: number | string | { $numberDecimal?: string } | null;
  total?: number | string | { $numberDecimal?: string } | null;
  totalAmount?: number | string | { $numberDecimal?: string } | null;
  amount?: number | string | { $numberDecimal?: string } | null;
  price?: number | string | { $numberDecimal?: string } | null;
}

export interface YieldTransaction {
  asset?: string | null;
  currency?: string | null;
  transactionType?: string | null;
  transactionSubtype?: string | null;
  amount?: number | string | { $numberDecimal?: string } | null;
  valueInZar?: number | string | { $numberDecimal?: string } | null;
  transactionRef?: YieldTransactionRef | null;
}

export interface YieldPortfolioMetrics {
  averageCost: number;
  netInvested: number;
  unrealizedPnL: number;
  portfolioDiversity: number;
  totalPnL: number;
  pnlPercentage: number;
  totalDeposited: number;
  totalWithdrawn: number;
}

export const EMPTY_YIELD_MARKET_DATA: YieldMarketData = {
  price: null,
  priceInZar: null,
  marketCap: null,
  volume24h: null,
  circulatingSupply: null,
  rank: null,
  maxSupply: null,
  totalSupply: null,
  allTimeHigh: null,
  athPercentage: null,
  roiPercentage: null,
  percentChange30d: null,
  description: null,
};

export const EMPTY_YIELD_PORTFOLIO_METRICS: YieldPortfolioMetrics = {
  averageCost: 0,
  netInvested: 0,
  unrealizedPnL: 0,
  portfolioDiversity: 0,
  totalPnL: 0,
  pnlPercentage: 0,
  totalDeposited: 0,
  totalWithdrawn: 0,
};

const normalizeSymbol = (value?: string | null): string =>
  value?.toUpperCase() || "";

const isMatchingYieldTransaction = (
  transaction: YieldTransaction,
  assetSymbol: string
): boolean => {
  const normalizedAssetSymbol = normalizeSymbol(assetSymbol);

  return [
    transaction.asset,
    transaction.currency,
    transaction.transactionRef?.incomingAsset,
    transaction.transactionRef?.outgoingAsset,
  ]
    .map((value) => normalizeSymbol(value))
    .includes(normalizedAssetSymbol);
};

const getTransactionAmount = (transaction: YieldTransaction): number => {
  const subtype = transaction.transactionSubtype?.toLowerCase();

  if (transaction.transactionType === "UserTrade") {
    if (subtype === "buy") {
      return parseNumericValue(
        transaction.transactionRef?.incomingAmount ??
          transaction.transactionRef?.total ??
          transaction.transactionRef?.totalAmount ??
          transaction.amount
      );
    }

    if (subtype === "sell") {
      return parseNumericValue(
        transaction.transactionRef?.outgoingAmount ??
          transaction.transactionRef?.total ??
          transaction.transactionRef?.totalAmount ??
          transaction.amount
      );
    }
  }

  return parseNumericValue(
    transaction.amount ??
      transaction.transactionRef?.total ??
      transaction.transactionRef?.totalAmount ??
      transaction.transactionRef?.amount
  );
};

const getTransactionZarValue = (
  transaction: YieldTransaction,
  amount: number
): number => {
  if (transaction.transactionRef?.amount != null) {
    return parseNumericValue(transaction.transactionRef.amount);
  }

  if (transaction.valueInZar != null) {
    return parseNumericValue(transaction.valueInZar);
  }

  if (transaction.transactionRef?.price != null) {
    return amount * parseNumericValue(transaction.transactionRef.price);
  }

  if (transaction.transactionRef?.totalAmount != null) {
    return parseNumericValue(transaction.transactionRef.totalAmount);
  }

  return 0;
};

export const getYieldMarketDataBySymbol = (
  marketAssets?: MarketAssetsMap | null,
  assetSymbol = "ETH"
): YieldMarketData | null => {
  if (!marketAssets) {
    return null;
  }

  const normalizedAssetSymbol = assetSymbol.toUpperCase();
  const marketEntry = Object.entries(marketAssets).find(
    ([symbol, asset]) =>
      symbol?.toUpperCase() === normalizedAssetSymbol ||
      asset?.info?.asset?.toUpperCase() === normalizedAssetSymbol
  );
  const marketData = marketEntry ? marketEntry[1] : null;

  if (!marketData) {
    return null;
  }

  return {
    price: marketData.price ?? null,
    priceInZar: marketData.priceInZar ?? null,
    marketCap: marketData.market_cap ?? null,
    volume24h: marketData.volume_24h ?? null,
    circulatingSupply: marketData.circulating_supply ?? null,
    rank: marketData.market_cap_rank ?? null,
    maxSupply: marketData.max_supply ?? null,
    totalSupply: marketData.total_supply ?? null,
    allTimeHigh: marketData.ath ?? null,
    athPercentage: marketData.ath_percentage ?? null,
    roiPercentage:
      marketData.roi?.percentage ?? marketData.roi?.percent ?? null,
    percentChange30d: marketData.percent_change_30d ?? null,
    description: marketData.info?.cmc?.description ?? null,
  };
};

export const getEthYieldMarketData = (
  marketAssets?: MarketAssetsMap | null
): YieldMarketData | null => getYieldMarketDataBySymbol(marketAssets, "ETH");

export const calculateYieldPortfolioMetrics = ({
  transactions,
  assetSymbol,
  assetBalance,
  totalPortfolioValue = 0,
}: {
  transactions?: YieldTransaction[] | null;
  assetSymbol: string;
  assetBalance?: YieldBalanceAsset | null;
  totalPortfolioValue?: number | string | null;
}): YieldPortfolioMetrics => {
  if (!assetSymbol) {
    return EMPTY_YIELD_PORTFOLIO_METRICS;
  }

  const matchingTransactions = (transactions || []).filter((transaction) =>
    isMatchingYieldTransaction(transaction, assetSymbol)
  );

  if (matchingTransactions.length === 0 && !assetBalance) {
    return EMPTY_YIELD_PORTFOLIO_METRICS;
  }

  let totalBuyAmount = 0;
  let totalBuyCost = 0;
  let totalSellAmount = 0;
  let totalSellValue = 0;

  matchingTransactions.forEach((transaction) => {
    const subtype = transaction.transactionSubtype?.toLowerCase();
    const amount = getTransactionAmount(transaction);
    const zarValue = getTransactionZarValue(transaction, amount);

    if (
      subtype === "buy" ||
      subtype === "deposit" ||
      subtype === "incoming" ||
      subtype === "receive"
    ) {
      totalBuyAmount += amount;
      totalBuyCost += zarValue || 0;
      return;
    }

    if (
      subtype === "sell" ||
      subtype === "withdrawal" ||
      subtype === "outgoing" ||
      subtype === "send"
    ) {
      totalSellAmount += amount;
      totalSellValue += zarValue || 0;
    }
  });

  const currentBalance = parseNumericValue(assetBalance?.availableAmount);
  const currentValue = parseNumericValue(
    assetBalance?.valueInZar ?? assetBalance?.valueInLocalCurrency
  );
  const netAmount = totalBuyAmount - totalSellAmount;
  const averageBuyPrice =
    totalBuyAmount > 0 ? totalBuyCost / totalBuyAmount : 0;
  const realizedPnL = totalSellValue - averageBuyPrice * totalSellAmount;
  const netInvested = totalBuyCost - averageBuyPrice * totalSellAmount;
  const averageCost = netAmount > 0 ? netInvested / netAmount : averageBuyPrice;
  const unrealizedPnL = currentValue - averageCost * currentBalance;
  const totalPnL = unrealizedPnL + realizedPnL;
  const pnlPercentage = netInvested > 0 ? (totalPnL / netInvested) * 100 : 0;
  const normalizedPortfolioValue = parseNumericValue(totalPortfolioValue);
  const portfolioDiversity =
    normalizedPortfolioValue > 0
      ? (currentValue / normalizedPortfolioValue) * 100
      : 0;

  return {
    averageCost: averageCost || 0,
    netInvested: netInvested || 0,
    unrealizedPnL: unrealizedPnL || 0,
    portfolioDiversity: portfolioDiversity || 0,
    totalPnL: totalPnL || 0,
    pnlPercentage: pnlPercentage || 0,
    totalDeposited: totalBuyCost || 0,
    totalWithdrawn: totalSellValue || 0,
  };
};
